// src/utils/ttmDimensions.js
// Canvas 2D rendering for committed measurements (tick + running value),
// the active-placement reticle, and the detected QR outline.
// No React deps. Pure drawing.
//
// All sizes are passed a `k` factor (canvas-pixels per on-screen-pixel) so
// markers look the same on screen regardless of the source photo resolution.
// Compute it in your draw loop:
//   const rect = canvas.getBoundingClientRect();
//   const dispScale = rect.width > 0 ? rect.width / canvas.width : 1;
//   const k = 1 / (dispScale || 1);

export const TTM_HALO = "rgba(255,255,255,0.85)";

// Centralized colors. Defaults lean to the brand / instrument look.
// To go back to your original neon set, swap the two hexes noted below.
export const DIM_COLORS = {
  stick:  "#d9a300",   // amber — calibration reference (was "yellow")
  width:  "#0057b8",   // brand blue (was "#00e0ff")
  length: "#b5179e",   // refined magenta (was "#ff00c8")
};

function ttmRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Measure a centered label box (does not draw). Returns geometry + font size.
export function ttmPillRect(ctx, text, cx, cy, k) {
  const fontPx = 14 * k;
  const padX = 9 * k, padY = 5 * k;
  ctx.font = `600 ${fontPx}px -apple-system, system-ui, sans-serif`;
  const w = ctx.measureText(text).width + padX * 2;
  const h = fontPx + padY * 2;
  return { x: cx - w / 2, y: cy - h / 2, w, h, fontPx };
}

// Draw a value pill given a box from ttmPillRect.
export function ttmDrawPill(ctx, box, text, color, k) {
  ttmRoundRect(ctx, box.x, box.y, box.w, box.h, 6 * k);
  ctx.lineWidth = 3 * k; ctx.strokeStyle = TTM_HALO; ctx.stroke();   // halo casing
  ctx.fillStyle = color; ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.font = `600 ${box.fontPx}px -apple-system, system-ui, sans-serif`;
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(text, box.x + box.w / 2, box.y + box.h / 2 + 0.5 * k);
}

// Committed dimension line: connecting line + perpendicular tick caps + an
// upright value pill that sits clear of the line. Pass the SAME `occupied`
// array to every line in a frame so labels dodge one another.
export function ttmDrawDimension(ctx, p1, p2, label, color, k, occupied = []) {
  const lineW = 2.5 * k;
  const tickLen = 22 * k;
  const dx = p2.x - p1.x, dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;

  // Perpendicular unit vector, then forced to a DETERMINISTIC side so the
  // label position no longer depends on the order the two points were tapped:
  //   horizontal-ish lines -> label sits ABOVE
  //   vertical-ish lines   -> label sits to the LEFT
  let nx = -dy / len, ny = dx / len;
  if (Math.abs(ny) > 1e-6) {
    if (ny > 0) { nx = -nx; ny = -ny; }      // point up (negative y)
  } else if (nx > 0) {
    nx = -nx; ny = -ny;                       // point left for vertical lines
  }

  const strokeSet = (w, c) => {
    ctx.lineCap = "round"; ctx.lineWidth = w; ctx.strokeStyle = c;
    ctx.beginPath(); ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
    [p1, p2].forEach((p) => {
      ctx.beginPath();
      ctx.moveTo(p.x - nx * tickLen / 2, p.y - ny * tickLen / 2);
      ctx.lineTo(p.x + nx * tickLen / 2, p.y + ny * tickLen / 2);
      ctx.stroke();
    });
  };
  strokeSet(lineW + 4 * k, TTM_HALO);   // legibility casing under the color
  strokeSet(lineW, color);

  if (!label || label === "—") return;

  const mid = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
  const probe = ttmPillRect(ctx, label, 0, 0, k);
  // Offset to the label CENTER = tick tip + half the label + a clear gap,
  // so the pill never overlaps its own tick caps.
  const gap = 8 * k;
  const off = tickLen / 2 + probe.h / 2 + gap;

  const centerAt = (s) => ({ x: mid.x + nx * off * s, y: mid.y + ny * off * s });
  const rectAt = (c) => ({ x: c.x - probe.w / 2, y: c.y - probe.h / 2, w: probe.w, h: probe.h });
  const hits = (r) => occupied.some(
    (o) => r.x < o.x + o.w && r.x + r.w > o.x && r.y < o.y + o.h && r.y + r.h > o.y
  );

  // Preferred side is +1 (the forced side above/left); flip only on collision.
  let c = centerAt(1), leader = false;
  if (hits(rectAt(c))) {
    const alt = centerAt(-1);
    if (!hits(rectAt(alt))) c = alt;
    else { c = centerAt(1.9); leader = true; }
  }
  if (leader) {
    ctx.lineWidth = 1 * k; ctx.strokeStyle = color;
    ctx.setLineDash([3 * k, 3 * k]);
    ctx.beginPath(); ctx.moveTo(mid.x, mid.y); ctx.lineTo(c.x, c.y); ctx.stroke();
    ctx.setLineDash([]);
  }
  const box = ttmPillRect(ctx, label, c.x, c.y, k);
  ttmDrawPill(ctx, box, label, color, k);
  occupied.push(box);
}

// Active-placement reticle: thin ring + gap crosshair. The exact point is the
// intersection, never covered. Use it at the cursor and the anchored point.
export function ttmDrawReticle(ctx, p, color, k) {
  const r = 11 * k;
  const ring = (w, col) => {
    ctx.lineWidth = w; ctx.strokeStyle = col; ctx.lineCap = "round";
    ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.stroke();
    const seg = (ax, ay, bx, by) => { ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke(); };
    seg(p.x - 19 * k, p.y, p.x - 5 * k, p.y);
    seg(p.x + 5 * k, p.y, p.x + 19 * k, p.y);
    seg(p.x, p.y - 19 * k, p.x, p.y - 5 * k);
    seg(p.x, p.y + 5 * k, p.x, p.y + 19 * k);
  };
  ring(3.5 * k, TTM_HALO);
  ring(1.8 * k, color);
}

// Outline the detected QR (four corners in image pixels) + a value pill above
// its top edge, so the user sees exactly what calibration locked onto.
export function ttmDrawQrBox(ctx, corners, label, color, k, occupied = []) {
  const { tl, tr, br, bl } = corners;
  const pts = [tl, tr, br, bl];

  const strokePoly = (w, c) => {
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.lineWidth = w; ctx.strokeStyle = c;
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
    ctx.closePath(); ctx.stroke();
  };
  strokePoly(2.5 * k + 4 * k, TTM_HALO);
  strokePoly(2.5 * k, color);

  if (!label) return;

  // Place the label above the top edge (tl -> tr), forced to the "up" side.
  const mx = (tl.x + tr.x) / 2, my = (tl.y + tr.y) / 2;
  const ex = tr.x - tl.x, ey = tr.y - tl.y;
  const len = Math.hypot(ex, ey) || 1;
  let nx = ey / len, ny = -ex / len;
  if (ny > 0) { nx = -nx; ny = -ny; }
  const probe = ttmPillRect(ctx, label, 0, 0, k);
  const off = probe.h / 2 + 10 * k;
  const box = ttmPillRect(ctx, label, mx + nx * off, my + ny * off, k);
  ttmDrawPill(ctx, box, label, color, k);
  occupied.push(box);
}
